import { z } from 'zod';
import { paginationMetaSchema } from '../common/pagination';
import { moneySchema } from '../money';
import { articleUnitSchema } from './article.schema';

/**
 * Inventur (ADR 0058): Stichtags-Bestandsaufnahme über mehrere Artikel.
 * ENTWURF = befüllbar/zählbar, ABGESCHLOSSEN = Korrekturen gebucht, unveränderlich.
 */
export const inventoryCountStatusSchema = z.enum(['ENTWURF', 'ABGESCHLOSSEN']);
export type InventoryCountStatus = z.infer<typeof inventoryCountStatusSchema>;

export const createInventoryCountSchema = z.object({
  title: z.string().trim().max(200).optional(),
});
export type CreateInventoryCountInput = z.infer<typeof createInventoryCountSchema>;

export const updateInventoryCountSchema = z.object({
  title: z.string().trim().max(200).nullable().optional(),
});
export type UpdateInventoryCountInput = z.infer<typeof updateInventoryCountSchema>;

/** Einzelnen Artikel per ID zur Zählliste hinzufügen. */
export const addInventoryCountItemSchema = z.object({
  articleId: z.string().uuid(),
});
export type AddInventoryCountItemInput = z.infer<typeof addInventoryCountItemSchema>;

/**
 * Komplette Hauptwarengruppe zur Zählliste hinzufügen (alle aktiven, unblockierten
 * `STANDARD`-Lagerartikel dieser Gruppe) — oder ohne `commodityMainGroup` ALLE
 * solchen Artikel. Bereits enthaltene Artikel werden nicht doppelt hinzugefügt.
 */
export const addInventoryCountItemsByGroupSchema = z.object({
  commodityMainGroup: z.number().int().nullable().optional(),
});
export type AddInventoryCountItemsByGroupInput = z.infer<typeof addInventoryCountItemsByGroupSchema>;

/** Ist-Menge einer Position erfassen (oder mit `null` wieder auf „nicht gezählt" zurücksetzen). */
export const setInventoryCountItemSchema = z.object({
  countedQty: z.number().min(0).nullable(),
  note: z.string().trim().max(500).nullable().optional(),
});
export type SetInventoryCountItemInput = z.infer<typeof setInventoryCountItemSchema>;

export const listInventoryCountsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  status: inventoryCountStatusSchema.optional(),
  q: z.string().trim().max(200).optional(),
});
export type ListInventoryCountsQuery = z.infer<typeof listInventoryCountsQuerySchema>;

export const inventoryCountItemResponseSchema = z.object({
  id: z.string().uuid(),
  inventoryCountId: z.string().uuid(),
  articleId: z.string().uuid(),
  articleNumber: z.string(),
  articleName: z.string(),
  unit: articleUnitSchema,
  systemQtyAtCount: z.number(),
  countedQty: z.number().nullable(),
  countedAt: z.string().datetime().nullable(),
  countedByUserId: z.string().uuid().nullable(),
  countedByUserName: z.string().nullable(),
  note: z.string().nullable(),
  bookedMovementId: z.string().uuid().nullable(),
  /**
   * Wertdifferenz (Ist − Soll) × EK-Preis — wie jeder Preis über
   * `inventory.viewprices` redigiert (null = keine Berechtigung oder kein EK gepflegt).
   */
  differenceValue: moneySchema.nullable(),
});
export type InventoryCountItemResponse = z.infer<typeof inventoryCountItemResponseSchema>;

export const inventoryCountResponseSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  number: z.string(),
  title: z.string().nullable(),
  status: inventoryCountStatusSchema,
  completedAt: z.string().datetime().nullable(),
  completedByUserId: z.string().uuid().nullable(),
  completedByUserName: z.string().nullable(),
  /** Zusammenfassung für die Listen-/Kopfansicht, ohne alle Positionen zu laden. */
  itemCount: z.number().int(),
  countedCount: z.number().int(),
  differingCount: z.number().int(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  items: z.array(inventoryCountItemResponseSchema),
});
export type InventoryCountResponse = z.infer<typeof inventoryCountResponseSchema>;

export const inventoryCountListSchema = z.object({
  data: z.array(inventoryCountResponseSchema.omit({ items: true })),
  meta: paginationMetaSchema,
});
export type InventoryCountListResponse = z.infer<typeof inventoryCountListSchema>;
